import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiCheck, FiEdit2 } from 'react-icons/fi';
import { Button, Col, Input, Modal, Row, Table, ModalBody } from 'reactstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import FormTooltip from 'components/Inputs/FormTooltip';

const isEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

function EmailIgnoredList({
  initialEmails = [],
  onEmailsChange,
  isModalOpen,
  toggleModal
}) {
  const [emails, setEmails] = useState(initialEmails);
  const [newEmail, setNewEmail] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const editInputRef = useRef(null);

  useEffect(() => {
    if (editIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editIndex]);

  const handleAddEmail = () => {
    if (isEmail(newEmail)) {
      const updatedEmails = [...emails, newEmail];
      setEmails(updatedEmails);
      setNewEmail('');
      onEmailsChange(updatedEmails);
    } else {
      toast.error('Por favor, insira um email válido', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const handleEditEmail = () => {
    if (isEmail(editEmail)) {
      const updatedEmails = emails.map((email, i) =>
        i === editIndex ? editEmail : email
      );
      setEmails(updatedEmails);
      setEditIndex(null);
      setEditEmail('');
      onEmailsChange(updatedEmails);
    } else {
      toast.error('Por favor, insira um email válido', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = emails.filter((_, i) => i !== index);
    setEmails(updatedEmails);
    onEmailsChange(updatedEmails);
  };

  const handleNewEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleEditEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditEmail();
    }
  };

  const closeModal = () => {
    toggleModal();
  };

  return (
    <Modal
      className="modal-dialog-centered"
      isOpen={isModalOpen}
      toggle={closeModal}
      style={{ width: '90vw', maxWidth: ' 50rem' }}
    >
      <div className="modal-header">
        <h6
          className="modal-title"
          id="modal-title-default"
          style={{
            width: '100%'
          }}
        >
          Definir lista de emails a serem ignorados pelo sistema
          <FormTooltip text="Todos os emails cadastrados aqui não serão respondidos pelo sistema. Ou seja, se algum usuário enviar um email com um desses emails, o sistema não irá responder." />
        </h6>
        <button
          aria-label="Close"
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={closeModal}
        >
          <span aria-hidden={true}>x</span>
        </button>
      </div>
      <ModalBody>
        <Row>
          <Col md="12">
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th className="w-75">Email</th>
                  <th className="w-25 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {emails.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="text-center text-muted">
                      Nenhum email ignorado
                    </td>
                  </tr>
                ) : (
                  emails.map((email, index) => (
                    <tr key={index}>
                      {editIndex === index ? (
                        <>
                          <td
                            style={{
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            <Input
                              type="text"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              onKeyDown={handleEditEmailKeyDown}
                              aria-label="Editar email"
                              onFocus={(e) => e.target.select()}
                              innerRef={editInputRef}
                            />
                          </td>
                          <td
                            className="text-center"
                            style={{
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            <div className="d-flex justify-content-center gap-2">
                              <Button
                                color="success"
                                onClick={handleEditEmail}
                                aria-label="Salvar email"
                                size="sm"
                              >
                                <FiCheck size={12} />
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => setEditIndex(null)}
                                aria-label="Cancelar edição"
                                size="sm"
                              >
                                <FaTrash size={12} />
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td
                            style={{
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            {email}
                          </td>
                          <td
                            className="text-center"
                            style={{
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            <div className="d-flex justify-content-center gap-2">
                              <Button
                                size="sm"
                                color="info"
                                onClick={() => {
                                  setEditIndex(index);
                                  setEditEmail(email);
                                }}
                                aria-label="Editar email"
                              >
                                <FiEdit2 size={12} />
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => handleRemoveEmail(index)}
                                aria-label="Remover email"
                              >
                                <FaTrash size={12} />
                              </Button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <div className="d-flex mt-3 align-items-center">
              <Input
                type="text"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={handleNewEmailKeyDown}
                placeholder="Adicionar email"
                aria-label="Novo email"
                className="mr-2"
              />
              <Button
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
                color="success"
                onClick={handleAddEmail}
                aria-label="Adicionar email"
              >
                <FaPlus className="mr-1" /> Adicionar
              </Button>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
}

export default EmailIgnoredList;
